/**
 * Veggiet Site Fixes
 * 1) Klaviyo popup patch — copy corrections (invalid date, "up to" qualifier),
 *    visible 44px close button, ESC/overlay dismiss. Scoped strictly to the
 *    Klaviyo POPUP Form container; no other DOM is touched.
 * 2) Cart page discount notice — shows the discount code that will apply at
 *    checkout (reads Shopify discount_code cookie or vg-promo-code storage).
 *
 * Pure logic is exported for tests (site-fixes.test.js).
 */
(function (global) {
  'use strict';

  /* ── Pure logic ─────────────────────────────────────────── */

  function patchPopupCopy(text) {
    if (!text) return text;
    var out = text.replace(/june\s+31st/gi, 'June 30th');
    if (!/up\s*to\s+45\s*%/i.test(out)) {
      out = out.replace(/45\s*%\s*off/i, function (match, offset, str) {
        var atStart = str.slice(0, offset).trim() === '';
        return (atStart ? 'Up to ' : 'up to ') + match;
      });
    }
    return out;
  }

  function getAppliedDiscountCode(cookieStr, storedVal) {
    var code = null;
    if (cookieStr) {
      var parts = cookieStr.split(';');
      for (var i = 0; i < parts.length; i++) {
        var eq = parts[i].indexOf('=');
        if (eq === -1) continue;
        var name = parts[i].slice(0, eq).trim();
        if (name === 'discount_code') {
          var v = parts[i].slice(eq + 1).trim();
          try { v = decodeURIComponent(v); } catch (e) { /* keep raw */ }
          if (v) { code = v; break; }
        }
      }
    }
    if (!code && storedVal && String(storedVal).trim()) {
      code = String(storedVal).trim();
    }
    return code ? code.toUpperCase() : null;
  }

  function shouldShowStickyCta(ctaIntersecting, viewportWidth) {
    return !ctaIntersecting && viewportWidth < 750;
  }

  /* ── Real discount application (Approach A) ──────────────────
     The promo box was visual-only — it never set the real Shopify discount,
     so checkout charged full price. We route a valid code through the EXISTING
     /discount/<code> link (the same mechanism the email link uses), which sets
     a JS-readable `discount_code` cookie that carries to checkout. */

  /* Is the real Shopify discount for `code` currently active? (cookie set) */
  function cookieHasDiscount(cookieStr, code) {
    if (!cookieStr || !code) return false;
    var target = code.toString().trim().toLowerCase();
    if (!target) return false;
    var parts = cookieStr.split(';');
    for (var i = 0; i < parts.length; i++) {
      var eq = parts[i].indexOf('=');
      if (eq === -1) continue;
      if (parts[i].slice(0, eq).trim() !== 'discount_code') continue;
      var v = parts[i].slice(eq + 1).trim();
      try { v = decodeURIComponent(v); } catch (e) { /* keep raw */ }
      return v.toLowerCase() === target;
    }
    return false;
  }

  /* Build the /discount/<code> URL that sets the cookie then returns to the
     current page, preserving existing query params (UTM) and ensuring the
     ?discount= param is present so the visual re-applies after the redirect. */
  function buildDiscountRedirectUrl(code, pathname, search) {
    var params = new URLSearchParams(search || '');
    params.set('discount', (code || '').toString().toLowerCase());
    var redirect = pathname + '?' + params.toString();
    return '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(redirect);
  }

  /* Decide what to do when a valid code is applied:
       'active'   → real discount already set; show visual, do nothing else
       'navigate' → not set yet, first attempt; go through /discount/
       'failed'   → not set after an attempt (expired/invalid); show error, no loop */
  function discountApplyDecision(cookieStr, code, attempted) {
    if (cookieHasDiscount(cookieStr, code)) return 'active';
    if (attempted) return 'failed';
    return 'navigate';
  }

  /* ── Pre-order CTA gating ───────────────────────────────────
     Pre-order products are sold with inventory policy "continue" (oversell
     allowed), so they are ALWAYS orderable. The add-to-cart button must never
     be disabled by a variant's `available` flag while pre-order mode is on —
     otherwise a stale/edge-cached page rendered when the variant was briefly
     unavailable (e.g. before the policy was switched to "continue") shows a
     grey, unclickable "Pre-Order Now" button even though checkout works. In
     normal (non pre-order) mode the availability flag still governs. */
  function shouldDisableCta(available, preorderMode) {
    if (preorderMode) return false;
    return !available;
  }

  /* ── PDP flavor detection ───────────────────────────────────
     Matcha & black bean are SEPARATE products with no flavor variant, so the
     buy box derives the flavor from the product handle/title (or a flavor
     radio value when one exists). Black bean → true, matcha/empty → false. */
  function isBlackBeanFlavor(str) {
    var s = (str || '').toString().toLowerCase();
    return s.indexOf('black') !== -1 || s.indexOf('bean') !== -1 || s.indexOf('soritae') !== -1;
  }

  /* ── Bundle → variant mapping (E1) ──────────────────────────
     Bundles are real Shopify variants ("1 box" … "6 boxes") carrying the
     bundle price (v10 MSP). Selecting a bundle must switch the variant,
     NOT multiply the 1-box quantity — otherwise bundle pricing never
     reaches checkout. Returns null when no bundle variant exists so the
     caller can keep the legacy qty behaviour. */
  var BOX_VALUE = /^\s*(\d+)\s*box(es)?\s*$/i;

  function findBundleVariant(variants, options, qty) {
    if (!variants || !variants.length) return null;
    var idx = -1;
    if (options && options.length) {
      for (var i = 0; i < options.length; i++) {
        var name = (options[i] || '').toString().toLowerCase();
        if (name.indexOf('bundle') !== -1 || name.indexOf('box') !== -1) { idx = i; break; }
      }
    }
    if (idx === -1) {
      var first = variants[0].options || [];
      for (var j = 0; j < first.length; j++) {
        if (BOX_VALUE.test(first[j])) { idx = j; break; }
      }
    }
    if (idx === -1) return null;
    for (var k = 0; k < variants.length; k++) {
      var m = BOX_VALUE.exec(((variants[k].options || [])[idx] || '').toString());
      if (m && parseInt(m[1], 10) === qty) return variants[k];
    }
    return null;
  }

  /* Cart contract: a bundle variant already contains N boxes → qty 1.
     Legacy fallback (no bundle variant): keep 1-box × qty. */
  function cartQuantityFor(bundleVariantFound, qty) {
    return bundleVariantFound ? 1 : qty;
  }

  /* E5: the one-time price element shows the bundle TOTAL — the static
     "per box" label is wrong for multi-box bundles. */
  function priceUnitLabel(qty, originalLabel) {
    return qty > 1 ? 'for ' + qty + ' boxes' : originalLabel;
  }

  /* Klaviyo renamed its modal container ("POPUP Form" → "Form Dialog",
     2026-06), which silently broke aria-label-only targeting. Match any
     Klaviyo-owned dialog (kl-private reset class + dialog role) plus the
     legacy label, but never theme dialogs like cart-notification. */
  function isKlaviyoModal(attrs) {
    if (!attrs) return false;
    var aria = attrs.ariaLabel || '';
    var role = attrs.role || '';
    var cls = (attrs.className || '').toString();
    if (aria === 'POPUP Form') return true;
    return role === 'dialog' && cls.indexOf('kl-private') !== -1;
  }

  var KL_MODAL_SELECTOR = '[aria-label="POPUP Form"], [role="dialog"][class*="kl-private"]';

  /* A click target counts as a "close" affordance when its text is an ✕/×/x
     glyph or its text/aria-label mentions "close". Used by the global close
     delegation so that ANY close tap reliably dismisses an open Klaviyo popup
     (Klaviyo's own button, a stray off-screen close, etc.) instead of dead-
     clicking. Pure + case-insensitive. */
  function isCloseControl(text, aria) {
    var t = (text || '').toString().trim();
    if (t === '✕' || t === '×' || t === 'x' || t === 'X') return true;
    var hay = (t + ' ' + (aria || '')).toLowerCase();
    return hay.indexOf('close') !== -1;
  }

  /* The close button must sit ON the visible popup card (the cream box),
     not the full-screen dialog wrapper — otherwise it floats at the screen
     corner, detached from the box. A card is meaningfully smaller than the
     overlay but still a substantial content box. */
  function isPopupCardRect(modalW, modalH, w, h) {
    if (!modalW || !modalH || !w || !h) return false;
    if (w < 200 || h < 150) return false;          /* too small: buttons, rows */
    if (h > modalH - 40) return false;             /* full-height wrapper/backdrop */
    return true;
  }

  /* ── Browser wiring ─────────────────────────────────────── */

  var KL_DISMISS_KEY = 'vg-kl-dismissed';

  var KL_COOLDOWN_KEY = 'vg-kl-cooldown-until';
  var KL_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; /* after a dismiss, re-show at most once per 7 days (frequency reduction) */
  function klaviyoDismissedThisSession() {
    try {
      if (sessionStorage.getItem(KL_DISMISS_KEY) === '1') return true;
      var until = parseInt(localStorage.getItem(KL_COOLDOWN_KEY) || '0', 10);
      return !!until && Date.now() < until;
    } catch (e) { return false; }
  }
  function markKlaviyoDismissed() {
    try { sessionStorage.setItem(KL_DISMISS_KEY, '1'); } catch (e) { /* private mode */ }
    try { localStorage.setItem(KL_COOLDOWN_KEY, String(Date.now() + KL_COOLDOWN_MS)); } catch (e) { /* private mode */ }
  }

  /* Force-hide every open Klaviyo popup. We do NOT depend on Klaviyo's own
     close button working (on mobile it is hidden / off-viewport / unreliable),
     so the visual close is guaranteed by hiding the top-level wrapper. We also
     best-effort click the real Klaviyo close first so its frequency-capping
     cookie still gets set, but never rely on it. `remember` records the
     dismissal for the session so the popup is not re-shown on every PDP. */
  function dismissKlaviyoPopup(remember) {
    var modals = document.querySelectorAll(KL_MODAL_SELECTOR);
    var hidAny = false;
    for (var i = 0; i < modals.length; i++) {
      var form = modals[i];
      var native = form.querySelector(
        'button[aria-label="Close dialog"]:not(.vg-popup-close), button[aria-label*="close" i]:not(.vg-popup-close)'
      );
      if (native) { try { native.click(); } catch (e) { /* ignore */ } }
      var wrapper = form;
      while (wrapper.parentElement && wrapper.parentElement !== document.body) {
        wrapper = wrapper.parentElement;
      }
      wrapper.style.setProperty('display', 'none', 'important');
      wrapper.setAttribute('data-vg-kl-hidden', '1');
      hidAny = true;
    }
    if (remember && hidAny) markKlaviyoDismissed();
    return hidAny;
  }

  function patchPopupForm(form) {
    var walker = document.createTreeWalker(form, NodeFilter.SHOW_TEXT, null);
    var node;
    while ((node = walker.nextNode())) {
      var patched = patchPopupCopy(node.textContent);
      if (patched !== node.textContent) node.textContent = patched;
    }

    /* Our own always-visible 44px close button inside the popup card. It calls
       the shared, reliable dismiss (never depends on Klaviyo's own button) and
       remembers the dismissal for the session. */
    var dismiss = function () { dismissKlaviyoPopup(true); };

    /* Anchor to the visible card: outermost descendant that is clearly
       smaller than the overlay and contains the form content. Falls back
       to the dialog itself if no card-like element is found (e.g. while the
       entrance animation is still running) — later observer runs re-anchor
       the button once the card has its real size. */
    var anchor = form;
    var content = form.querySelector('form, input[type="email"], button[type="submit"]');
    var modalRect = form.getBoundingClientRect();
    var divs = form.querySelectorAll('div');
    for (var i = 0; i < divs.length; i++) {
      var r = divs[i].getBoundingClientRect();
      if (isPopupCardRect(modalRect.width, modalRect.height, r.width, r.height)
          && (!content || divs[i].contains(content))) {
        anchor = divs[i];
        break;
      }
    }

    var ours = form.querySelector('.vg-popup-close');
    if (!ours) {
      ours = document.createElement('button');
      ours.type = 'button';
      ours.className = 'vg-popup-close';
      ours.setAttribute('aria-label', 'Close');
      ours.innerHTML = '✕';
      ours.addEventListener('click', dismiss);
      if (getComputedStyle(anchor).position === 'static') anchor.style.position = 'relative';
      anchor.appendChild(ours);
    } else if (anchor !== form && ours.parentElement !== anchor) {
      /* card appeared after initial fallback attach — move onto the card */
      if (getComputedStyle(anchor).position === 'static') anchor.style.position = 'relative';
      anchor.appendChild(ours);
    }

    if (!form.hasAttribute('data-vg-dismiss-wired')) {
      form.setAttribute('data-vg-dismiss-wired', '1');
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && document.body.contains(form)) dismiss();
      });
      var overlay = form.parentElement;
      if (overlay) {
        overlay.addEventListener('click', function (e) {
          if (e.target === overlay) dismiss();
        });
      }
    }
  }

  function injectPopupStyles() {
    if (document.getElementById('vg-popup-fix-style')) return;
    var style = document.createElement('style');
    style.id = 'vg-popup-fix-style';
    /* Stylesheet !important survives Klaviyo re-renders, unlike inline styles */
    style.textContent = '\n'
      /* Hide Klaviyo's native close button (it renders off-viewport on mobile)
         ONLY once our replacement ✕ is actually attached — the :has() guard
         guarantees the popup can never end up with zero close affordances.
         (Clarity 6/30–7/7: ~1.3k dead clicks on un-closable popups whose
         native close we had hidden while the replacement never attached.)
         Browsers without :has() simply show both buttons — still closable. */
      + '[aria-label="POPUP Form"]:has(.vg-popup-close) button[aria-label="Close dialog"]:not(.vg-popup-close),\n'
      + '[aria-label="POPUP Form"]:has(.vg-popup-close) button[aria-label*="lose"]:not(.vg-popup-close),\n'
      + '[role="dialog"][class*="kl-private"]:has(.vg-popup-close) button[aria-label="Close dialog"]:not(.vg-popup-close),\n'
      + '[role="dialog"][class*="kl-private"]:has(.vg-popup-close) button[aria-label*="lose"]:not(.vg-popup-close) {\n'
      + '  display: none !important;\n'
      + '}\n'
      /* Our own always-visible 44px close button, top-right of the popup card */
      + '[aria-label="POPUP Form"] .vg-popup-close,\n'
      + '[role="dialog"][class*="kl-private"] .vg-popup-close {\n'
      + '  display: flex !important; align-items: center !important; justify-content: center !important;\n'
      + '  position: absolute !important; top: 10px !important; right: 10px !important;\n'
      + '  width: 44px !important; height: 44px !important;\n'
      + '  z-index: 2147483647 !important; background: #ffffff !important;\n'
      + '  border-radius: 50% !important; border: 1px solid rgba(0,0,0,0.2) !important;\n'
      + '  box-shadow: 0 2px 8px rgba(0,0,0,0.18) !important; cursor: pointer !important;\n'
      + '  font-size: 18px !important; line-height: 1 !important; color: #0c0b0b !important;\n'
      + '  padding: 0 !important; margin: 0 !important;\n'
      + '}\n';
    document.head.appendChild(style);
  }

  /* Global capture-phase close delegation: whenever a Klaviyo popup is open,
     a tap on ANY close affordance (Klaviyo's own button, our X, or a stray
     off-screen close that would otherwise dead-click) reliably dismisses the
     popup. Scoped so it never swallows the cart notification / drawer close. */
  function initCloseDelegation() {
    if (document.documentElement.hasAttribute('data-vg-kl-deleg')) return;
    document.documentElement.setAttribute('data-vg-kl-deleg', '1');
    /* ESC works even when the per-form wiring never attached (see the
       observer-starvation note in initPopupPatch) — global, wired once. */
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (!document.querySelector(KL_MODAL_SELECTOR)) return;
      dismissKlaviyoPopup(true);
    });
    document.addEventListener('click', function (e) {
      if (!document.querySelector(KL_MODAL_SELECTOR)) return; /* no popup open */
      var el = e.target;
      var hit = null;
      for (var i = 0; i < 4 && el && el !== document.body; i++) {
        if (el.closest && el.closest('cart-notification, cart-drawer')) return;
        /* text only counts on leaf nodes (the actual ✕ button/icon) so a
           container whose body text merely contains "close" never matches */
        var leafText = (el.children && el.children.length === 0) ? el.textContent : '';
        var aria = el.getAttribute && el.getAttribute('aria-label');
        if (isCloseControl(leafText, aria)) { hit = el; break; }
        el = el.parentElement;
      }
      if (hit) {
        if (dismissKlaviyoPopup(true)) { e.preventDefault(); e.stopPropagation(); }
        return;
      }
      /* Backdrop / outside-card tap -> dismiss. This is the #1 dead click:
         shoppers tap the dark area around the popup expecting it to close.
         Guarded to the identified visible card so taps INSIDE the form never
         close it. Only fires when the card is known (our close button attached). */
      var modalEl = document.querySelector(KL_MODAL_SELECTOR);
      if (!modalEl) return;
      if (e.target.closest && e.target.closest('cart-notification, cart-drawer')) return;
      var wrap = modalEl;
      while (wrap.parentElement && wrap.parentElement !== document.body) wrap = wrap.parentElement;
      if (!wrap.contains(e.target)) return; /* click outside the popup overlay entirely */
      var vgClose = modalEl.querySelector('.vg-popup-close');
      var card = (vgClose && vgClose.parentElement) || modalEl.querySelector('form');
      if (!card) return; /* card not identified yet -> don't risk a false close */
      var cr = card.getBoundingClientRect();
      if (cr.width < 40 || cr.height < 40) return;
      var insideCard = e.clientX >= cr.left && e.clientX <= cr.right && e.clientY >= cr.top && e.clientY <= cr.bottom;
      if (insideCard) return; /* clicked inside the card -> keep open */
      if (dismissKlaviyoPopup(true)) { e.preventDefault(); e.stopPropagation(); }
    }, true);
  }

  function initPopupPatch() {
    injectPopupStyles();
    initCloseDelegation();
    var pending = false;
    var run = function () {
      pending = false;
      /* If the shopper already closed the popup this session, keep it closed
         instead of re-trapping them on every PDP. */
      if (klaviyoDismissedThisSession()) { dismissKlaviyoPopup(false); return; }
      document.querySelectorAll(KL_MODAL_SELECTOR).forEach(function (el) {
        if (isKlaviyoModal({
          ariaLabel: el.getAttribute('aria-label'),
          role: el.getAttribute('role'),
          className: el.className,
        })) patchPopupForm(el);
      });
    };
    run();
    /* Schedule via setTimeout, NOT requestAnimationFrame: RAF suspends on
       hidden/occluded pages (in-app WebView preloads, background tabs) and a
       starved callback left `pending` stuck — the popup then appeared with
       its native close hidden and no replacement attached (un-closable).
       setTimeout still fires on hidden pages (merely throttled). */
    var observer = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      setTimeout(run, 150);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    /* entrance animations resize the card without DOM mutations —
       re-run so the close button can re-anchor onto the settled card */
    setTimeout(run, 1500);
    setTimeout(run, 3500);
    /* Belt-and-braces ensure loop: near-zero cost when no popup is present,
       guarantees the patch eventually lands no matter how the form arrived. */
    setInterval(run, 1200);
  }

  function initCartNotice() {
    var slot = document.querySelector('[data-vg-cart-notice]');
    if (!slot) return;
    var stored = null;
    try { stored = sessionStorage.getItem('vg-promo-code'); } catch (e) { /* private mode */ }
    var code = getAppliedDiscountCode(document.cookie, stored);
    if (!code) return;
    var codeEl = slot.querySelector('[data-vg-cart-notice-code]');
    if (codeEl) codeEl.textContent = code;
    slot.hidden = false;
  }

  /* ── Exports ────────────────────────────────────────────── */

  var api = {
    patchPopupCopy: patchPopupCopy,
    getAppliedDiscountCode: getAppliedDiscountCode,
    shouldShowStickyCta: shouldShowStickyCta,
    isKlaviyoModal: isKlaviyoModal,
    isPopupCardRect: isPopupCardRect,
    findBundleVariant: findBundleVariant,
    cartQuantityFor: cartQuantityFor,
    priceUnitLabel: priceUnitLabel,
    isBlackBeanFlavor: isBlackBeanFlavor,
    cookieHasDiscount: cookieHasDiscount,
    buildDiscountRedirectUrl: buildDiscountRedirectUrl,
    discountApplyDecision: discountApplyDecision,
    shouldDisableCta: shouldDisableCta,
    isCloseControl: isCloseControl,
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  if (typeof document !== 'undefined' && global) {
    global.VGSiteFixes = api;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        initPopupPatch();
        initCartNotice();
      });
    } else {
      initPopupPatch();
      initCartNotice();
    }
  }
})(typeof window !== 'undefined' ? window : null);
